"use client"

import { useState } from "react"
import { usePathname, useRouter, useSearchParams } from "next/navigation"

import { SearchFacets } from "@lib/data/fitment"
import { Button, Text, clx } from "@modules/common/components/ui"
import EngineSelect from "@modules/ymm/components/engine-select"

type ResultsFiltersProps = {
  facets: SearchFacets
  engines: string[]
  category?: string
  brand?: string
  engine: string
  priceMin?: string
  priceMax?: string
}

const FacetList = ({
  title,
  options,
  selected,
  onSelect,
  testId,
}: {
  title: string
  options: Record<string, number>
  selected?: string
  onSelect: (value: string | null) => void
  testId: string
}) => {
  const entries = Object.entries(options).sort((a, b) =>
    a[0].localeCompare(b[0])
  )

  if (!entries.length) {
    return null
  }

  return (
    <div className="flex flex-col gap-y-2" data-testid={testId}>
      <Text className="txt-compact-small-plus text-ui-fg-muted uppercase tracking-wide">
        {title}
      </Text>
      <ul className="flex flex-col gap-y-1">
        {entries.map(([name, count]) => {
          const isActive = selected === name

          return (
            <li key={name}>
              <button
                type="button"
                onClick={() => onSelect(isActive ? null : name)}
                aria-pressed={isActive}
                className={clx(
                  "flex w-full items-center justify-between gap-x-2 rounded-md px-2 py-1 text-left text-sm transition-colors hover:bg-ui-bg-subtle",
                  isActive
                    ? "font-semibold text-ui-fg-base bg-ui-bg-subtle"
                    : "text-ui-fg-subtle"
                )}
              >
                <span className="truncate">{name}</span>
                <span className="text-xs text-ui-fg-muted">{count}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// Left-hand filter rail for the vehicle results page. All state lives in the
// URL search params so filtered views are shareable and back-button friendly.
const ResultsFilters = ({
  facets,
  engines,
  category,
  brand,
  engine,
  priceMin,
  priceMax,
}: ResultsFiltersProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [isOpen, setIsOpen] = useState(false)
  const [minInput, setMinInput] = useState(priceMin ?? "")
  const [maxInput, setMaxInput] = useState(priceMax ?? "")

  const updateParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams)

    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "") {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    }

    // Filter changes restart pagination.
    params.delete("page")

    const queryString = params.toString()
    router.push(queryString ? `${pathname}?${queryString}` : pathname)
  }

  const handleApplyPrice = () => {
    updateParams({
      price_min: minInput.trim() || null,
      price_max: maxInput.trim() || null,
    })
  }

  const brandFacet = facets.brand ?? {}
  const showBrand = Object.keys(brandFacet).length > 1

  const hasActiveFilters = Boolean(
    category || brand || engine || priceMin || priceMax
  )

  return (
    <aside className="w-full small:w-64 small:shrink-0" data-testid="vehicle-filters">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="small:hidden flex w-full items-center justify-between rounded-md border border-ui-border-base px-4 py-2 text-sm font-medium text-ui-fg-base"
        aria-expanded={isOpen}
        data-testid="vehicle-filters-toggle"
      >
        Filters
        <span aria-hidden>{isOpen ? "−" : "+"}</span>
      </button>

      <div
        className={clx(
          "flex-col gap-y-6 mt-4 small:mt-0",
          isOpen ? "flex" : "hidden small:flex"
        )}
        data-testid="vehicle-filters-panel"
      >
        {engines.length > 0 && (
          <div className="flex flex-col gap-y-2">
            <Text className="txt-compact-small-plus text-ui-fg-muted uppercase tracking-wide">
              Engine
            </Text>
            <EngineSelect
              engines={engines}
              value={engine}
              // "all" is an explicit sentinel: without it, dropping the param
              // would fall back to the engine saved with the active vehicle.
              onChange={(value) => updateParams({ engine: value || "all" })}
              data-testid="vehicle-filters-engine"
            />
          </div>
        )}

        <FacetList
          title="Category"
          options={facets.category ?? {}}
          selected={category}
          onSelect={(value) => updateParams({ category: value })}
          testId="vehicle-filters-category"
        />

        {showBrand && (
          <FacetList
            title="Brand"
            options={brandFacet}
            selected={brand}
            onSelect={(value) => updateParams({ brand: value })}
            testId="vehicle-filters-brand"
          />
        )}

        <div className="flex flex-col gap-y-2" data-testid="vehicle-filters-price">
          <Text className="txt-compact-small-plus text-ui-fg-muted uppercase tracking-wide">
            Price
          </Text>
          <div className="flex items-center gap-x-2">
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="Min"
              value={minInput}
              onChange={(e) => setMinInput(e.target.value)}
              aria-label="Minimum price"
              className="w-full rounded-md border border-ui-border-base bg-ui-bg-subtle px-3 py-2 text-sm outline-none focus:border-ui-border-interactive"
              data-testid="vehicle-filters-price-min"
            />
            <span className="text-ui-fg-muted" aria-hidden>
              –
            </span>
            <input
              type="number"
              min="0"
              inputMode="numeric"
              placeholder="Max"
              value={maxInput}
              onChange={(e) => setMaxInput(e.target.value)}
              aria-label="Maximum price"
              className="w-full rounded-md border border-ui-border-base bg-ui-bg-subtle px-3 py-2 text-sm outline-none focus:border-ui-border-interactive"
              data-testid="vehicle-filters-price-max"
            />
          </div>
          <Button
            size="small"
            variant="secondary"
            onClick={handleApplyPrice}
            data-testid="vehicle-filters-price-apply"
          >
            Apply
          </Button>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setMinInput("")
              setMaxInput("")
              updateParams({
                category: null,
                brand: null,
                engine: engine ? "all" : null,
                price_min: null,
                price_max: null,
              })
            }}
            className="text-sm text-ui-fg-subtle underline hover:text-ui-fg-base text-left"
            data-testid="vehicle-filters-clear"
          >
            Clear all filters
          </button>
        )}
      </div>
    </aside>
  )
}

export default ResultsFilters
